import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth, Role, Tier } from '../contexts/AuthContext';
import { Shield, Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: Role[];
  requiredTiers?: Tier[];
}

export default function ProtectedRoute({
  children,
  requiredRoles,
  requiredTiers,
}: ProtectedRouteProps) {
  const { user, isPending, hasRole, hasTier } = useAuth();
  const location = useLocation();

  // TEMPORARY DEV BYPASS — remove before production (Phase 6)
  // Lets you preview protected pages without logging in during development.
  if (import.meta.env.DEV) {
    return <>{children}</>;
  }

  if (isPending) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-950">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (requiredRoles && !hasRole(requiredRoles)) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-950">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Access Denied</h2>
          <p className="text-slate-400">
            You don't have permission to access this page. Required role:{' '}
            {requiredRoles.join(' or ')}.
          </p>
        </div>
      </div>
    );
  }

  if (requiredTiers && !hasTier(requiredTiers)) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-950">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-amber-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Upgrade Required</h2>
          <p className="text-slate-400">
            This feature requires a {requiredTiers.join(' or ')} plan.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
